import { Mobile, ServiceList, panelType } from '@bimser/common';
import { IFlexPanel, IFlexPanelAnimationEndEvent } from '@bimser/components';
import { IBaseAction, IBaseMutableAction, INormalized } from '@bimser/core';
import { List, Map } from 'immutable';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { panelCloseRequestTransaction, panelWillUnMountTransaction } from '../../common/actions';
import * as commonActionTypes from '../../common/actions/actionTypes';
import { WebInterfaceBaseState } from '../../common/base/state';
import { WebInterfaceAction } from '../../common/entities';
import * as ActionTypesMain from '../actions/actionTypes';
import closeFlexPanelAddAnimationCssAction from '../actions/closeFlexPanelAddAnimationCssAction';
import closeFlexPanelListAction from '../actions/closeFlexPanelListAction';
import openStartupPanelTransactionAction from '../actions/openStartupPanelTransactionAction';
import resizeFlexPanelTransactionAction from '../actions/resizeFlexPanelTransactionAction';
import setActiveFlexPanelAction from '../actions/setActiveFlexPanelAction';
import showFlexPanelInLeftTransactionAction from '../actions/showFlexPanelInLeftTransactionAction';
import { FlexPanel } from '../entities/FlexPanelFactory';
import { leftMenuSetActiveNodeKey } from '../layouts/left-menu/actions';
import { clickBreadCrumbTransaction } from './clickBreadCrumbTransaction';
import getStateForSaga from './sagaHelper';

export default function* watchCloseFlexPanelTransaction() {
	yield takeEvery(ActionTypesMain.CLOSE_FLEX_PANEL_TRANSACTION, closeFlexPanelTransactionManager);
}

export function* closeFlexPanelTransactionManager(sagaAction: IBaseAction<WebInterfaceAction>) {
	const flexpanel: FlexPanel = sagaAction.payload.data.flexpanel;
	const panelId = flexpanel.id;
	const fromPanel: boolean = sagaAction.payload.data.fromPanel;
	const disableWillOpenStartUpPanel: boolean = sagaAction.payload.data.data === 'disableStartUp';
	const fromPanelId: string = fromPanel ? panelId : '';
	const state: WebInterfaceBaseState = yield select();
	const sagaState = getStateForSaga(state);
	const panelSize = sagaState.result.size;
	const panelIndex = sagaState.result.indexOf(panelId);
	const panels = sagaState.entities;
	const panel = panels.get(panelId);
	const closingPanelType: panelType = panel?.getIn(['componentProps', 0, 'props', 'panelType']);
	const closeCheckInObject: panelType = panel?.getIn([
		'componentProps',
		0,
		'props',
		'hasCheckInObject'
	]);

	if (panelIndex < 0) {
		return;
	}

	Mobile.MobileView.closeFlexPanel(closingPanelType);
	Mobile.MobileView.modalFormClosing({ formPanelId: panelId });

	if (closeCheckInObject) {
		const secretKey = panel?.getIn([
			'componentProps',
			0,
			'props',
			'flowStartParameters',
			'documents',
			0,
			'createOptions',
			'secretKey'
		]);
		yield ServiceList.main.WebObject.CheckOutForm.call({ secretKey: secretKey });
	}

	for (let index = panelSize - 1; index >= panelIndex; index--) {
		const currentPanelId = sagaState.result.get(index);

		if (!panels.get(currentPanelId).canHandleCloseEvent) {
			const fastEventId: panelType = panel.getIn([
				'componentProps',
				0,
				'props',
				'fastEventId'
			]);
			const fastEvents = panel.getIn([
				'componentProps',
				0,
				'props',
				'flowStartParameters',
				'events'
			]);

			const fastEvent = fastEvents?.find(fEvent => fEvent.id == fastEventId);

			yield call(
				closeFlexPanelTransaction,
				sagaState.result.get(index),
				fromPanelId,
				false,
				disableWillOpenStartUpPanel,
				fastEvent
			);
			yield call(
				willItClose,
				panels,
				currentPanelId,
				fromPanelId,
				sagaAction.payload.data.data
			);
			continue;
		}

		if (!panels.get(currentPanelId).isModified) {
			yield call(
				closeFlexPanelIfNotModified,
				index,
				panelIndex,
				sagaState,
				fromPanelId,
				disableWillOpenStartUpPanel
			);
			yield call(
				willItClose,
				panels,
				currentPanelId,
				fromPanelId,
				sagaAction.payload.data.data
			);
			continue;
		}

		if (panelSize !== index + 1) {
			yield call(
				closeFlexPanelTransaction,
				sagaState.result.get(index + 1),
				fromPanelId,
				false,
				disableWillOpenStartUpPanel
			);
		}

		yield call(clickBreadCrumbTransaction, { payload: { id: currentPanelId } });
		yield put(panelCloseRequestTransaction({ panelId: currentPanelId }));

		const response: IBaseAction<WebInterfaceAction> = yield take(
			commonActionTypes.PANEL_CLOSE_RESPONSE_TRANSACTION
		);
		if (response.payload?.panelId === currentPanelId && response.payload.data) {
			yield call(
				closeFlexPanelTransaction,
				currentPanelId,
				fromPanelId,
				false,
				disableWillOpenStartUpPanel
			);
			yield call(
				willItClose,
				panels,
				currentPanelId,
				fromPanelId,
				sagaAction.payload.data.data
			);
		} else {
			break;
		}
	}
}

function* closeFlexPanelIfNotModified(
	index: number,
	panelIndex: number,
	sagaState: INormalized<Map<string, FlexPanel>, List<string>>,
	fromPanelId: string,
	disableWillOpenStartUpPanel: boolean
) {
	if (index === panelIndex) {
		yield call(
			closeFlexPanelTransaction,
			sagaState.result.get(index),
			fromPanelId,
			false,
			disableWillOpenStartUpPanel
		);
	}
}

function* willItClose(
	panels: Map<string, FlexPanel>,
	currentPanelId: string,
	fromPanelId: string,
	_data: any
) {
	yield put(panelWillUnMountTransaction({ panelId: currentPanelId, data: _data }));
}

export function* closeFlexPanelTransaction(
	panelId: string,
	fromPanelId = '',
	stopLeftSideAnimation = false,
	disableWillOpenStartUpPanel = false,
	fastEvent = null
) {
	let totalSize: number = 0;
	let panelCounter: number = 0;
	let panelSize: number = 0;
	let shownPanelActualSize: number = 0;
	const state: WebInterfaceBaseState = yield select();
	let onScreenPanelId: string;
	let activePanelId: string;
	const doesntClosedPanels: string[] = [];
	// Tıklanan panelin indexini bul
	const panelIndex = getStateForSaga(state).result.indexOf(panelId);
	let willShownPanelId: string;
	// bir önceki panelin Id'si
	willShownPanelId = getStateForSaga(state).result.get(panelIndex - 1);
	// Bir önceki paneli aktif et.
	yield put(setActiveFlexPanelAction(willShownPanelId));

	const panelList: Array<IFlexPanel> = [];
	// Kapatılan panelden sonraki panelleri listeye ekle ve css animasyonlarını koy
	if (panelIndex !== -1) {
		for (let index = getStateForSaga(state).result.size - 1; index >= panelIndex; index--) {
			const panelId = getStateForSaga(state).result.get(index);
			const panel = getStateForSaga(state).entities.get(panelId);
			if (panel.isOnScreen) {
				onScreenPanelId = panel.id;
			}

			if (panel.isActive) {
				activePanelId = panel.id;
			}

			yield put(closeFlexPanelAddAnimationCssAction(panel));
			panelList.push(panel);
		}
	}

	if (!onScreenPanelId || !activePanelId) {
		for (let index = panelIndex - 1; index >= 0; index--) {
			const panelId = getStateForSaga(state).result.get(index);
			const panel = getStateForSaga(state).entities.get(panelId);
			if (panel.isOnScreen) {
				onScreenPanelId = panel.id;
			}
			if (panel.isActive) {
				activePanelId = panel.id;
			}
			if (onScreenPanelId && activePanelId) {
				break;
			}
		}
	}

	const activePanelIndex = getStateForSaga(state).result.indexOf(activePanelId);
	if (
		getStateForSaga(state).result.size > 0 &&
		!stopLeftSideAnimation &&
		!(activePanelIndex < panelIndex - 1)
	) {
		// Önceki panellerin size'larını ve görünürlüklerini ayarla
		for (let index = panelIndex - 1; index >= 0; index--) {
			const panelId = getStateForSaga(state).result.get(index);
			const panel = getStateForSaga(state).entities.get(panelId);
			panelCounter++;

			if (panelCounter == 1) {
				panelSize = panel.initialSize;
			} else {
				panelSize = panel.size;
			}

			totalSize += panelSize;
			// Önceki panellerden size'ı 3 olana kadarkiler üzerinde işlem yap.
			if (totalSize <= 3) {
				// Panellerin size'ları değişmedi ise işlem yapma
				if (panel.size != panel.initialSize) {
					// ilk paneli initialSize'ına resize et
					if (panelCounter == 1) {
						let newSize: number;

						if (fastEvent) {
							newSize = fastEvent.form ? panel.initialSize : panel.size;
						} else {
							newSize = panel.initialSize;
						}

						yield put(
							resizeFlexPanelTransactionAction(
								panel.set('size', newSize).set('prevSize', panel.size)
							)
						);
					}
					//Diğer panelleri sığacak şekilde hesapla ve ona göre resize et
					else {
						shownPanelActualSize = 3 - (totalSize - panelSize);
						totalSize = shownPanelActualSize + totalSize;
						yield put(
							resizeFlexPanelTransactionAction(
								panel.set('size', shownPanelActualSize)
							)
						);
					}
				}
				// Paneller ekranda zaten gözüküyorsa işlem yapma
				if (!panel.isVisible || !panel.isOnScreen) {
					yield put(showFlexPanelInLeftTransactionAction(panel));
				}
			} else {
				break;
			}
		}
	}
	let animationExpectedPanel: string =
		totalSize === 3 ? willShownPanelId || onScreenPanelId : panelId;
	const animationPanel = getStateForSaga(state).entities.get(animationExpectedPanel);
	if (animationPanel && !animationPanel.isOnScreen) {
		animationExpectedPanel = onScreenPanelId;
	}

	const panel = getStateForSaga(state).entities.get(panelId);
	if (panel?.isOnScreen && !panel.ghostPanel) {
		yield take((action: IBaseMutableAction<IFlexPanelAnimationEndEvent>) => {
			if (
				action.type == ActionTypesMain.ANIMATION_END_PANEL &&
				action.payload.panel.id == animationExpectedPanel
			) {
				return true;
			}
		});
	}

	// Toplu olarak panelleri sil
	yield put(closeFlexPanelListAction(panelList));
	if (getStateForSaga(state).result.size == 1 && !disableWillOpenStartUpPanel) {
		yield put(openStartupPanelTransactionAction());
	}
	yield put(leftMenuSetActiveNodeKey({ data: null }));

	return doesntClosedPanels;
}
